import { configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { combineReducers, type Reducer, type UnknownAction } from "redux";
import { persistReducer, persistStore } from "redux-persist";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import authReducer from "@/slices/auth-slice";
import { processStateSlice } from "@/slices/process-slice";
import { menuItemsSlice } from "@/slices/menu-items-slice";
import { colorModeSlice } from "@/slices/color-mode-slice";
import { discountsSlice } from "@/slices/discount-slice";
import { userOrdersSlice } from "@/slices/user-orders-slice";
import finalBillingSlice from "@/slices/final-billing-slice";
import checkoutUxSlice from "@/slices/checkout-ux-slice";
import checkoutSlice from "@/slices/checkout-slice";
import checkoutAddressSlice from "@/slices/checkout-address-slice";

const rootReducer = combineReducers({
  auth: authReducer,
  processState: processStateSlice.reducer,
  menuItems: menuItemsSlice.reducer,
  colorMode: colorModeSlice.reducer,
  discounts: discountsSlice.reducer,
  userOrders: userOrdersSlice.reducer,
  finalBilling: finalBillingSlice,
  checkoutUx: checkoutUxSlice,
  checkout: checkoutSlice,
  checkoutAddress: checkoutAddressSlice,
});

/**
 * Theme preference is owned by `rx-theme-style` / `rx-theme-mode` (see constants/theme-preference.ts).
 * colorMode is intentionally excluded so OS-derived legacy persist values cannot
 * override Classic Amber + Light defaults, and so we do not write theme keys until
 * the user changes Appearance settings.
 */
const persistConfig = {
  key: "root",
  storage,
  whitelist: [] as string[],
};

type AppReducerState = ReturnType<typeof rootReducer>;

/**
 * redux-persist typings model `Partial` + `PersistPartial` during rehydrate; at runtime (after PersistGate)
 * the app uses the full `AppReducerState`. Cast bridges the intentional mismatch (community pattern).
 */
const persistedReducer = persistReducer(persistConfig, rootReducer) as unknown as Reducer<
  AppReducerState,
  UnknownAction
>;

const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }),
});

export const persistor = persistStore(store);

export default store;

/** Use root reducer state shape (auth, menu, etc.). Cart lives in zustand (`useCartStore`). Persist adds _persist at runtime. */
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
